import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCcw } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

export default function SelfieCapture({ onCapture, disabled }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const start = async () => {
    setError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 640 },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
    } catch (e) {
      setError("Tidak dapat mengakses kamera. Izinkan akses kamera di browser.");
    }
  };

  const stop = () => {
    stream?.getTracks().forEach((t) => t.stop());
    setStream(null);
  };

  useEffect(() => () => stop(), []); // eslint-disable-line

  const capture = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const size = Math.min(v.videoWidth, v.videoHeight);
    c.width = size;
    c.height = size;
    const ctx = c.getContext("2d");
    // Center crop square
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    const dataUrl = c.toDataURL("image/jpeg", 0.7);
    setPreview(dataUrl);
    onCapture?.(dataUrl);
    stop();
  };

  const retake = () => {
    setPreview(null);
    onCapture?.(null);
    start();
  };

  return (
    <div className="border border-black/10 bg-white">
      <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between">
        <div className="label-tiny">Verifikasi Swafoto</div>
        <div className="text-xs text-gray-500">Wajib · Anti-kecurangan</div>
      </div>
      <div className="aspect-square bg-black relative overflow-hidden">
        {preview ? (
          <img data-testid={TESTIDS.cameraPreview} src={preview} alt="Selfie" className="w-full h-full object-cover" />
        ) : (
          <video
            ref={videoRef}
            data-testid={TESTIDS.cameraFeed}
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
          />
        )}
        {!stream && !preview && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
            Kamera belum aktif
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-t border-black/10">{error}</div>}
      <div className="p-3 flex items-center gap-2">
        {!preview && !stream && (
          <button
            data-testid={TESTIDS.cameraStart}
            onClick={start}
            disabled={disabled}
            className="inline-flex items-center gap-2 border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors text-sm font-semibold"
          >
            <Camera className="w-4 h-4" /> Aktifkan Kamera
          </button>
        )}
        {stream && !preview && (
          <button
            data-testid={TESTIDS.cameraCapture}
            onClick={capture}
            className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 hover:bg-black transition-colors text-sm font-semibold"
          >
            <Camera className="w-4 h-4" /> Ambil Swafoto
          </button>
        )}
        {preview && (
          <button
            data-testid={TESTIDS.cameraRetake}
            onClick={retake}
            className="inline-flex items-center gap-2 border border-black/40 px-4 py-2 hover:bg-black hover:text-white transition-colors text-sm font-semibold"
          >
            <RefreshCcw className="w-4 h-4" /> Ambil Ulang
          </button>
        )}
      </div>
    </div>
  );
}
